// AppContext.js
import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { financialService } from '../utils/api';
import { APP_CONFIG } from '../appConfig';

// Number of steps that belong to "Goal Planning" phase (everything up to and including TransitionCard).
// Steps 0..GOAL_STEPS_COUNT-1  → "Goal Planning X / GOAL_STEPS_COUNT"
// Steps GOAL_STEPS_COUNT..end  → "Asset Allocation X / ASSET_STEPS_COUNT"
// This constant must be kept in sync with the step list in Questionary.js.
export const GOAL_STEPS_COUNT = 11; // BirthYear..TransitionCard (inclusive)

const initialState = {
  // Auth
  isAuthenticated: false,
  otpSent: false,
  access_token: null,
  isLoading: false,

  // Personal
  clientName: '',
  email: '',
  phoneNumber: '',
  city: '',
  employmentNextAttempted: false,

  // Goal Planning
  age: 0,
  retirementAge: 0,
  birthYear: 0,
  yearsForRetirement: -1,
  monthlyExpensesInRetirement: 0,
  emergencyFunds: 0,
  riskProfile: '',
  netWorth: 0,
  fundingScore: 0,
  corpusRequired: 0,
  amountRequiredToday: 0,
  receiveReport: true,
  requestCallback: false,
  kidsInfo: [],
  kidsEducation: [],
  kidsPlanningValid: false,
  goals: [],

  // Goal submit tracking
  goalDataSubmitted: false,

  // Cash Flow
  salary: 0,
  spouseSalary: 0,
  otherIncome: 0,
  monthlyExpense: 0,
  oneTimeYearly: 0,
  annualInvestments: 0,
  sip: 0,

  // Investments — Debt
  bankBalance: 0,
  fixedDeposit: 0,
  retirementFund: 0,
  bonds: 0,
  debtMF: 0,

  // Investments — Equity
  equityMF: 0,
  hybridMF: 0,
  invits: 0,
  stocks: 0,
  pms: 0,
  unlisted: 0,

  // Investments — Global
  globalEquity: 0,
  globalMF: 0,
  globalBonds: 0,

  // Investments — Alternatives
  gold: 0,
  otherInvestments: 0,
  realEstate: 0,

  // Computed totals
  total_debt: 0,
  total_income: 0,
  total_equity: 0,
  total_alternative_funds: 0,
  total_global_equity: 0,
  high_liquid: 0,
  low_liquid: 0,
  post_retirement: 0,

  // Employment
  occupation: '',
  employer: '',
  dependents: 0,

  // Investment Experience
  objective: '',
  concerns: '',
  yearsInEquity: '',

  // Misc
  residenceType: '',
  insuranceStatus: '',
  isQualified: false,

  // Single stepper
  activeStep: 0,
};

const ActionTypes = {
  SET_AUTH_STATE: 'SET_AUTH_STATE',
  UPDATE_FIELD: 'UPDATE_FIELD',
  UPDATE_KIDS_INFO: 'UPDATE_KIDS_INFO',
  SET_ACTIVE_STEP: 'SET_ACTIVE_STEP',
  RESET_FORM: 'RESET_FORM',
  ADD_GOAL: 'ADD_GOAL',
  REMOVE_GOAL: 'REMOVE_GOAL',
  LOAD_USER_DATA: 'LOAD_USER_DATA',
  SET_LOADING: 'SET_LOADING',
};

// ---------------------------------------------------------------------------
// Map API response → state
// ---------------------------------------------------------------------------
const mapUserDataToState = (apiData) => {
  const m = {};

  if (apiData.userData?.length > 0) {
    const u = apiData.userData[0];
    m.email                      = u.email || '';
    m.city                       = u.city || '';
    m.birthYear                  = u.birth_year || 0;
    m.age                        = u.birth_year ? new Date().getFullYear() - u.birth_year : 0;
    m.riskProfile                = u.risk_profile || '';
    m.netWorth                   = u.net_worth || 0;
    m.emergencyFunds             = u.emergency_funds || 0;
    m.fundingScore               = u.funding_score || 0;
    m.retirementAge              = u.planned_retirement_age || 0;
    m.monthlyExpensesInRetirement= u.monthly_expenses_in_retirement || 0;
    m.requestCallback            = u.request_callback || false;
    m.receiveReport              = u.request_report ?? true;
    m.yearsForRetirement         = u.planned_retirement_age
      ? u.planned_retirement_age - (new Date().getFullYear() - (u.birth_year || 0))
      : -1;
    m.occupation    = u.occupation || '';
    m.employer      = u.employer || '';
    m.dependents    = u.dependents || 0;
    m.objective     = u.objective || '';
    m.concerns      = u.concerns || '';
    m.yearsInEquity = u.years_in_equity || '';
  }

  if (apiData.goals) {
    m.goals = apiData.goals.map((g) => ({
      id:           g.id,
      type:         g.goal_type,
      currentValue: g.current_value,
      targetYear:   g.target_date,
      FV_goal:      g.future_value,
    }));
  }

  if (apiData.children) {
    m.kidsInfo = apiData.children.map((c) => ({
      name:        c.name,
      yearOfBirth: c.year_of_birth,
      age:         c.age,
    }));
    m.kidsEducation = apiData.children.map((c) => ({
      name:        c.name,
      yearOfBirth: c.year_of_birth,
      Age:         c.age,
      ug:          c.ug_college,
      ug_fees:     c.ug_college_fees,
      ug_fees_FV:  c.ug_college_fees_fv,
      pg:          c.pg_college,
      pg_fees:     c.pg_college_fees,
      pg_fees_FV:  c.pg_college_fees_fv,
    }));
  }

  if (apiData.investments?.length > 0) {
    const inv       = apiData.investments[0];
    m.bankBalance   = inv.bank_balance || 0;
    m.fixedDeposit  = inv.fixed_deposit || 0;
    m.retirementFund= inv.retirement_fund || 0;
    m.bonds         = inv.bonds || 0;
    m.debtMF        = inv.debt_mf || 0;
    m.equityMF      = inv.equity_mf || 0;
    m.hybridMF      = inv.hybrid_mf || 0;
    m.invits        = inv.invits || 0;
    m.stocks        = inv.stocks || 0;
    m.pms           = inv.pms || 0;
    m.unlisted      = inv.unlisted || 0;
    m.globalEquity  = inv.global_equity || 0;
    m.globalBonds   = inv.global_bonds || 0;
    m.globalMF      = inv.global_mf || 0;
    m.realEstate    = inv.real_estate || 0;
    m.gold          = inv.gold || 0;
    m.otherInvestments = inv.other_investments || 0;
  }

  if (apiData.cashflow?.length > 0) {
    const cf          = apiData.cashflow[0];
    m.salary          = cf.salary || 0;
    m.spouseSalary    = cf.spouse_salary || 0;
    m.otherIncome     = cf.other_income || 0;
    m.monthlyExpense  = cf.monthly_expense || 0;
    m.oneTimeYearly   = cf.one_time_yearly || 0;
    m.annualInvestments = cf.annual_investments || 0;
    m.sip             = cf.sip || 0;
  }

  // Computed totals
  m.total_income           = (m.salary || 0) + (m.spouseSalary || 0) + (m.otherIncome || 0);
  m.total_debt             = (m.bankBalance || 0) + (m.fixedDeposit || 0) + (m.retirementFund || 0) + (m.bonds || 0) + (m.debtMF || 0);
  m.total_equity           = (m.equityMF || 0) + (m.stocks || 0) + (m.pms || 0) + (m.unlisted || 0);
  m.total_global_equity    = (m.globalEquity || 0) + (m.globalMF || 0) + (m.globalBonds || 0);
  m.total_alternative_funds= (m.gold || 0) + (m.realEstate || 0) + (m.otherInvestments || 0);
  m.high_liquid            = (m.bankBalance || 0) + (m.fixedDeposit || 0);
  m.low_liquid             = m.total_equity + m.total_alternative_funds;

  return m;
};

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------
const appReducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.SET_AUTH_STATE:
      return { ...state, ...action.payload };

    case ActionTypes.LOAD_USER_DATA:
      return { ...state, ...mapUserDataToState(action.payload), isLoading: false };

    case ActionTypes.SET_LOADING:
      return { ...state, isLoading: action.payload };

    case ActionTypes.UPDATE_FIELD:
      return { ...state, [action.field]: action.value };

    case ActionTypes.UPDATE_KIDS_INFO:
      return { ...state, kidsInfo: action.value };

    case ActionTypes.SET_ACTIVE_STEP:
      return { ...state, activeStep: action.value };

    case ActionTypes.ADD_GOAL:
      return { ...state, goals: [...state.goals, action.payload] };

    case ActionTypes.REMOVE_GOAL:
      return { ...state, goals: state.goals.filter((_, i) => i !== action.payload) };

    case ActionTypes.RESET_FORM:
      return { ...initialState, isAuthenticated: state.isAuthenticated };

    default:
      return state;
  }
};

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------
const AppContext = createContext();
const STORAGE_KEY = APP_CONFIG.persistedStorageKey;

export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Persist state to localStorage (skip activeStep so the user always starts fresh)
  useEffect(() => {
    const { activeStep, ...rest } = state;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(rest));
  }, [state]);

  const actions = {
    setAuthState: (authData) =>
      dispatch({ type: ActionTypes.SET_AUTH_STATE, payload: authData }),

    sendOTP: (userData) =>
      dispatch({
        type: ActionTypes.SET_AUTH_STATE,
        payload: {
          otpSent: true,
          clientName: userData.name,
          email: userData.email,
          phoneNumber: userData.phoneNo,
        },
      }),

    verifyOTP: async (accessToken, phoneNumber) => {
      dispatch({
        type: ActionTypes.SET_AUTH_STATE,
        payload: { isAuthenticated: true, access_token: accessToken, phoneNumber },
      });

      if (accessToken && phoneNumber) {
        dispatch({ type: ActionTypes.SET_LOADING, payload: true });
        try {
          const userData = await financialService.loadUserData(phoneNumber);
          if (userData) {
            dispatch({ type: ActionTypes.LOAD_USER_DATA, payload: userData });
          } else {
            dispatch({ type: ActionTypes.SET_LOADING, payload: false });
          }
        } catch (error) {
          console.error('Error loading user data:', error);
          dispatch({ type: ActionTypes.SET_LOADING, payload: false });
        }
      }
    },

    logout: () => {
      localStorage.removeItem(STORAGE_KEY);
      dispatch({ type: ActionTypes.RESET_FORM });
    },

    resetForm: () => {
      localStorage.removeItem(STORAGE_KEY);
      dispatch({ type: ActionTypes.RESET_FORM });
    },

    updateFormData: (field, value) =>
      dispatch({ type: ActionTypes.UPDATE_FIELD, field, value }),

    addGoal: (goal) => dispatch({ type: ActionTypes.ADD_GOAL, payload: goal }),
    removeGoal: (index) => dispatch({ type: ActionTypes.REMOVE_GOAL, payload: index }),
    updateKidsInfo: (kidsInfo) => dispatch({ type: ActionTypes.UPDATE_KIDS_INFO, value: kidsInfo }),

    // Stepper — single flat index
    setActiveStep: (val) => dispatch({ type: ActionTypes.SET_ACTIVE_STEP, value: val }),
    nextStep: () => dispatch({ type: ActionTypes.SET_ACTIVE_STEP, value: state.activeStep + 1 }),
    prevStep: () => dispatch({ type: ActionTypes.SET_ACTIVE_STEP, value: Math.max(state.activeStep - 1, 0) }),
  };

  return <AppContext.Provider value={{ state, actions }}>{children}</AppContext.Provider>;
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error('useAppContext must be used within an AppProvider');
  return context;
};

export { ActionTypes, AppContext };

